import { z } from "zod";

/**
 * « Synchroniser IABAKO » (Réglages › Imports): the ERP reads the previous tool's quotes and sales
 * with a headless browser, shows what it would take over, and writes only once confirmed.
 */
export const IABAKO_SYNC_STATUSES = ["fetching", "ready", "failed", "applied", "expired"] as const;
export type IabakoSyncStatus = (typeof IABAKO_SYNC_STATUSES)[number];

/**
 * Why a document read in IABAKO is left aside instead of being taken over: each one needs a person
 * (an article to link, a credit note to reconcile…). Nothing is guessed.
 */
export const IABAKO_UNRESOLVED_REASONS = [
  "unknownArticle",
  "ambiguousArticle",
  "unreadable",
  "unknownStatus",
  "missingSourceQuote",
  "creditNote",
  "payments",
  "totalGap",
  "notInIabako",
] as const;
export type IabakoUnresolvedReason = (typeof IABAKO_UNRESOLVED_REASONS)[number];

export interface IabakoUnresolved {
  number: string;
  reason: IabakoUnresolvedReason;
  /** The IABAKO label of the article, or the detail the reason names (a status, an amount…). */
  detail: string | null;
}

/** One line of the preview: what applying the run does to an IABAKO document. */
export interface IabakoPreviewDocument {
  number: string;
  kind: "quote" | "invoice";
  action: "create" | "update" | "unchanged";
  customerName: string;
  grossCents: number;
  /** An invoice already taken over: the payment received in IABAKO since, added on apply. */
  paymentCents?: number;
}

/**
 * Remembered once, reused by every later run. An article: the IABAKO line label (as IABAKO shows
 * it) to an ERP article, or `null` for a line without article (carriage, a service…). A customer:
 * the IABAKO name to the ERP customer when the names differ.
 */
export const iabakoMappingSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("article"),
    source: z.string().trim().min(1, "errors.field.required").max(300),
    variantId: z.uuid().nullable(),
  }),
  z.object({
    kind: z.literal("customer"),
    source: z.string().trim().min(1, "errors.field.required").max(300),
    customerId: z.uuid(),
  }),
]);
export type IabakoMappingInput = z.input<typeof iabakoMappingSchema>;
